import React from 'react';
import { Alert, Select } from 'antd';
import { Link } from 'react-router-dom';

const { Option } = Select;

export function companyAccountOptionLabel(acc) {
  const name = (acc?.account_name || '').trim();
  const code = (acc?.account_code || '').trim();
  if (name && code) {
    return `${name} | ${code}`;
  }
  return name || code;
}

/** Ant Design Select filter: match account name, code, or combined label. */
export function filterCompanyAccountOption(input, option) {
  const search = (input || '').toLowerCase().trim();
  if (!search) {
    return true;
  }
  const acc = option?.companyAccount;
  if (acc) {
    const name = String(acc.account_name || '').toLowerCase();
    const code = String(acc.account_code || '').toLowerCase();
    const label = companyAccountOptionLabel(acc).toLowerCase();
    return name.includes(search) || code.includes(search) || label.includes(search);
  }
  const text = String(option?.children || option?.label || '').toLowerCase();
  return text.includes(search);
}

export function appendReportSource(formData, reportSource) {
  formData.append('source', reportSource || 'journal');
}

export function applyReportSourceMeta(component, data) {
  component.setState({
    sourceUsed: data.source_used || '',
    mappingCount: Number(data.mapping_count || 0),
  });
}

export function JournalReportSourceControls({ reportSource, onSourceChange, style }) {
  return (
    <div style={style || { marginBottom: 12 }}>
      <span style={{ marginRight: 8 }}>Member data source:</span>
      <Select style={{ width: 240 }} value={reportSource} onChange={onSourceChange}>
        <Option value="journal">GeneralJournal (recommended)</Option>
        <Option value="transaction">Transaction (legacy)</Option>
      </Select>
    </div>
  );
}

export function JournalReportSourceAlert({ reportSource, sourceUsed, mappingCount, reportLabel }) {
  const label = reportLabel || 'Report';

  if (reportSource === 'journal' && mappingCount === 0) {
    return (
      <Alert
        type="error"
        showIcon
        style={{ marginBottom: 12 }}
        message="No ledger mappings configured"
        description={(
          <span>
            {label} cannot use GeneralJournal until subaccounts are mapped.{' '}
            <Link to="/companyprofile/#003">Company Profile → Member balances ledger map</Link>
          </span>
        )}
      />
    );
  }

  if (sourceUsed === 'transaction') {
    return (
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 12 }}
        message={`${label} used legacy Transaction data`}
        description="Configure member subaccount ledger mappings for journal-based figures."
      />
    );
  }

  if (sourceUsed === 'journal' && mappingCount > 0) {
    return (
      <Alert
        type="success"
        showIcon
        style={{ marginBottom: 12 }}
        message={`${label} from GeneralJournal (${mappingCount} subaccount mapping${mappingCount === 1 ? '' : 's'})`}
      />
    );
  }

  return null;
}

/** Member balances report — always GeneralJournal; no legacy source toggle. */
export function MemberBalancesJournalAlert({ mappingCount, reportLoadError, mappingsVerified }) {
  if (reportLoadError) {
    return (
      <Alert
        type="error"
        showIcon
        style={{ marginBottom: 12 }}
        message="Member balances report could not load"
        description={reportLoadError}
      />
    );
  }

  if (mappingsVerified && mappingCount === 0) {
    return (
      <Alert
        type="error"
        showIcon
        style={{ marginBottom: 12 }}
        message="No ledger mappings configured"
        description={(
          <span>
            Member balances are read from GeneralJournal only. Map subaccounts under{' '}
            <Link to="/companyprofile/#003">Company Profile → Member balances ledger map</Link>
            {' '}or balances will show as zero.
          </span>
        )}
      />
    );
  }

  if (mappingsVerified && mappingCount > 0) {
    return (
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message={`Member balances from GeneralJournal (${mappingCount} ledger mapping${mappingCount === 1 ? '' : 's'})`}
        description="Active members only. Totals aggregate mapped GeneralJournal lines. Click Show for per-member GL detail."
      />
    );
  }

  return null;
}

export function LedgerJournalInfoAlert() {
  return (
    <Alert
      type="info"
      showIcon
      style={{ marginBottom: 12 }}
      message="Ledger reports use GeneralJournal"
      description="Account totals include journal lines matched by company account (when set) and account name. Click Show on a row for GL line breakdown."
    />
  );
}

const MoneyCell = ({ value }) => (
  <span>
    {Number(value || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}
  </span>
);

export function trialBalanceAccountKey(item) {
  return `${item?.account_code || ''}|${item?.account || ''}`;
}

export function incomeStatementAccountKey(section, item) {
  return `${section}|${item?.no || ''}|${item?.account || ''}`;
}

export function TrialBalanceBreakdown({
  breakdown,
  reportBasis,
  summary,
  basisTitle = 'How the trial balance figure is built',
  summaryLabel = 'Trial balance',
  balanceLabel = 'Balance',
}) {
  if (!breakdown || breakdown.length === 0) {
    return (
      <Alert
        type="info"
        showIcon
        message="No GeneralJournal lines for this account in the selected period."
        style={{ margin: 8 }}
      />
    );
  }

  return (
    <div style={{ padding: '8px 12px', background: '#fafafa' }}>
      {reportBasis ? (
        <Alert type="info" showIcon message={basisTitle} description={reportBasis} style={{ marginBottom: 12 }} />
      ) : null}
      {summary ? (
        <div style={{ marginBottom: 12, fontSize: 13 }}>
          <strong>{summaryLabel}:</strong> Debit <MoneyCell value={summary.trial_balance_debit} />
          {' · '}Credit <MoneyCell value={summary.trial_balance_credit} />
          {summary.statement_balance != null ? (
            <>
              {' · '}{balanceLabel} <MoneyCell value={summary.statement_balance} />
            </>
          ) : null}
          {' · '}Total debits <MoneyCell value={summary.total_debit} />
          {' · '}Total credits <MoneyCell value={summary.total_credit} />
        </div>
      ) : null}
      {breakdown.map((section) => (
        <div key={section.section_key || section.section_label} style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>
            {section.section_label}
            {section.total != null ? (
              <>
                {' — section net '}
                <MoneyCell value={section.total} />
              </>
            ) : null}
            {section.line_count != null && section.line_count > 0 ? (
              <span style={{ fontWeight: 400, marginLeft: 8 }}>
                ({section.line_count} GL entr{section.line_count === 1 ? 'y' : 'ies'})
              </span>
            ) : null}
          </div>
          {section.balance_formula ? (
            <div style={{ fontSize: 12, color: '#555', marginBottom: 8 }}>{section.balance_formula}</div>
          ) : null}
          {section.note ? (
            <Alert type="warning" showIcon message={section.note} style={{ marginBottom: 8 }} />
          ) : null}
          {section.lines && section.lines.length > 0 ? (
            <table className="table table-sm table-bordered" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Jnl #</th>
                  <th>Voucher</th>
                  <th>Member</th>
                  <th>Description</th>
                  <th>Reference</th>
                  <th className="align-right">Debit</th>
                  <th className="align-right">Credit</th>
                  <th className="align-right">Net</th>
                  <th className="align-right">Running balance</th>
                </tr>
              </thead>
              <tbody>
                {section.lines.map((line, idx) => (
                  <tr key={`${section.section_key}-${line.id || idx}`}>
                    <td>{line.date || '—'}</td>
                    <td>{line.journal_no || '—'}</td>
                    <td>{line.voucher_no ? line.voucher_no : '—'}</td>
                    <td>{line.member_name || '—'}</td>
                    <td>{line.description || '—'}</td>
                    <td>{line.reference || '—'}</td>
                    <td className="align-right"><MoneyCell value={line.debit} /></td>
                    <td className="align-right"><MoneyCell value={line.credit} /></td>
                    <td className="align-right"><MoneyCell value={line.net} /></td>
                    <td className="align-right" style={{ fontWeight: 600 }}>
                      <MoneyCell value={line.running_balance} />
                    </td>
                  </tr>
                ))}
                <tr style={{ background: '#e6f7e6', fontWeight: 'bold' }}>
                  <td colSpan={8} className="align-right">
                    Section debits {section.period_debit_total} · credits {section.period_credit_total}
                    {section.sum_of_net != null ? ` → net ${section.sum_of_net}` : ''}
                  </td>
                  <td colSpan={2} />
                </tr>
              </tbody>
            </table>
          ) : null}
        </div>
      ))}
    </div>
  );
}
